const RARITY_COLORS = {
  common: '#888888',
  uncommon: '#4488ff',
  rare: '#8844ff',
  legendary: '#ff6600',
  default: '#444444',
};

const MAX_QUEUE = 5;
const CONSOLIDATION_WINDOW_MS = 400;
const STACK_GAP_PX = 74;
const ENTRY_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const WAITING_SELECTOR = '#pause, #fragment-draft, #tutorial-card';

let styleInjected = false;
let stackEl = null;
let activeEntry = null;
const queue = [];
let holdTimer = null;
let exitTimer = null;
let monitorRaf = 0;
let pausedResolver = null;

function injectStyles() {
  if (styleInjected || typeof document === 'undefined') return;
  styleInjected = true;
  const style = document.createElement('style');
  style.id = 'pickup-notification-styles';
  style.textContent = `
    .pickup-notif-stack {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 280px;
      pointer-events: none;
      z-index: 70;
      font-family: inherit, 'Courier New', monospace;
    }
    .pickup-notif {
      position: absolute;
      left: 0;
      top: 0;
      width: 280px;
      color: #fff;
      will-change: transform, opacity;
      filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.6));
      transform: translateY(0) scale(1);
      opacity: 1;
      transition: transform 160ms ${ENTRY_EASE}, opacity 160ms ${ENTRY_EASE};
    }
    .pickup-notif__top {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 4px;
    }
    .pickup-notif__icon-wrap {
      position: relative;
      width: 40px;
      height: 40px;
      background: #1a1a1a;
      border: 1px solid #555;
      border-radius: 3px;
      display: grid;
      place-items: center;
      flex: 0 0 40px;
    }
    .pickup-notif__icon {
      width: 28px;
      height: 28px;
      object-fit: contain;
      image-rendering: auto;
    }
    .pickup-notif__fallback {
      display: none;
      font-size: 20px;
      line-height: 1;
      color: #d8d8d8;
      font-weight: 700;
    }
    .pickup-notif__icon-wrap.is-fallback .pickup-notif__icon {
      display: none;
    }
    .pickup-notif__icon-wrap.is-fallback .pickup-notif__fallback {
      display: inline;
    }
    .pickup-notif__corner {
      position: absolute;
      width: 10px;
      height: 10px;
      border-color: #8e8e8e;
      border-style: solid;
      pointer-events: none;
    }
    .pickup-notif__corner--tl { top: 2px; left: 2px; border-width: 1px 0 0 1px; }
    .pickup-notif__corner--tr { top: 2px; right: 2px; border-width: 1px 1px 0 0; }
    .pickup-notif__corner--bl { bottom: 2px; left: 2px; border-width: 0 0 1px 1px; }
    .pickup-notif__corner--br { bottom: 2px; right: 2px; border-width: 0 1px 1px 0; }
    .pickup-notif__label {
      font-size: 11px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: 700;
      padding: 2px 8px;
      background: rgba(0, 0, 0, 0.45);
      color: #fff;
    }
    .pickup-notif__bar {
      position: relative;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 10px;
      border-radius: 0;
      overflow: hidden;
      border-bottom: 1px dotted rgba(255, 255, 255, 0.65);
    }
    .pickup-notif__name {
      font-size: 13px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 226px;
    }
    .pickup-notif__suffix {
      margin-left: 8px;
      font-size: 14px;
      font-weight: 800;
      flex: 0 0 auto;
    }
    .pickup-notif.is-queued {
      opacity: 0.5;
      transform: translateY(var(--queue-offset, 0px)) scale(0.85);
    }
  `;
  document.head.appendChild(style);
}

function ensureStack() {
  if (stackEl || typeof document === 'undefined') return;
  stackEl = document.createElement('div');
  stackEl.className = 'pickup-notif-stack';
  document.body.appendChild(stackEl);
}

function toDisplayName(entry) {
  if (entry.count > 1) return `${entry.count}x ${entry.baseName}`;
  return entry.originalName;
}

function normalizeItemName(name) {
  const raw = String(name || 'Unknown Item').trim() || 'Unknown Item';
  return raw.replace(/^\d+x\s+/i, '');
}

function normalizeEntry(data) {
  const now = performance.now();
  const rarityKey = String(data?.rarity || 'default').toLowerCase();
  const baseName = normalizeItemName(data?.itemName);
  return {
    icon: String(data?.icon || 'public/assets/icons/weapons/question.png'),
    label: String(data?.label || 'Obtained'),
    originalName: String(data?.itemName || 'Unknown Item'),
    baseName,
    itemName: String(data?.itemName || 'Unknown Item'),
    rarity: RARITY_COLORS[rarityKey] ? rarityKey : 'default',
    count: 1,
    lastUpdatedAt: now,
    key: `${String(data?.label || 'Obtained')}|${baseName}|${String(data?.rarity || 'default').toLowerCase()}`,
    el: null,
    nameEl: null,
    queueOffset: 0,
  };
}

function isElementVisible(el) {
  if (!el || el.classList.contains('hidden')) return false;
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || '1') > 0;
}

function isPaused() {
  if (typeof pausedResolver === 'function') {
    try {
      return !!pausedResolver();
    } catch {
      return false;
    }
  }
  if (typeof document === 'undefined') return false;
  const blockers = document.querySelectorAll(WAITING_SELECTOR);
  for (const blocker of blockers) {
    if (isElementVisible(blocker)) return true;
  }
  return false;
}

function createCard(entry) {
  const el = document.createElement('div');
  el.className = 'pickup-notif';
  el.innerHTML = `
    <div class="pickup-notif__top">
      <div class="pickup-notif__icon-wrap">
        <img src="${entry.icon}" class="pickup-notif__icon" alt="" />
        <span class="pickup-notif__fallback">?</span>
        <span class="pickup-notif__corner pickup-notif__corner--tl"></span>
        <span class="pickup-notif__corner pickup-notif__corner--tr"></span>
        <span class="pickup-notif__corner pickup-notif__corner--bl"></span>
        <span class="pickup-notif__corner pickup-notif__corner--br"></span>
      </div>
      <span class="pickup-notif__label"></span>
    </div>
    <div class="pickup-notif__bar">
      <span class="pickup-notif__name"></span>
      <span class="pickup-notif__suffix">!.</span>
    </div>
  `;
  const img = el.querySelector('.pickup-notif__icon');
  const iconWrap = el.querySelector('.pickup-notif__icon-wrap');
  const labelEl = el.querySelector('.pickup-notif__label');
  const barEl = el.querySelector('.pickup-notif__bar');
  const nameEl = el.querySelector('.pickup-notif__name');

  labelEl.textContent = entry.label || 'Obtained';
  nameEl.textContent = toDisplayName(entry);
  barEl.style.background = RARITY_COLORS[entry.rarity] || RARITY_COLORS.default;
  entry.nameEl = nameEl;

  img?.addEventListener('error', () => {
    iconWrap?.classList.add('is-fallback');
  }, { once: true });

  return el;
}

function removeEntryElement(entry) {
  if (entry?.el?.parentNode) entry.el.parentNode.removeChild(entry.el);
  entry.el = null;
  entry.nameEl = null;
}

function renderStack() {
  if (!stackEl) return;
  const rendered = [];
  if (activeEntry) rendered.push(activeEntry);
  rendered.push(...queue);

  for (const entry of rendered) {
    if (!entry.el) entry.el = createCard(entry);
    if (!stackEl.contains(entry.el)) stackEl.appendChild(entry.el);
  }

  for (const child of [...stackEl.children]) {
    const keep = rendered.some((entry) => entry.el === child);
    if (!keep) child.remove();
  }

  if (activeEntry?.el) {
    activeEntry.el.classList.remove('is-queued');
    activeEntry.el.style.setProperty('--queue-offset', '0px');
  }

  for (let i = 0; i < queue.length; i += 1) {
    const entry = queue[i];
    if (!entry.el) continue;
    entry.queueOffset = (i + 1) * STACK_GAP_PX;
    entry.el.classList.add('is-queued');
    entry.el.style.setProperty('--queue-offset', `${entry.queueOffset}px`);
  }
}

function clearTimers() {
  if (holdTimer) window.clearTimeout(holdTimer);
  if (exitTimer) window.clearTimeout(exitTimer);
  holdTimer = null;
  exitTimer = null;
}

function scheduleMonitor() {
  if (monitorRaf) return;
  const tick = () => {
    monitorRaf = 0;
    if (!isPaused()) {
      processQueue();
      return;
    }
    scheduleMonitor();
  };
  monitorRaf = window.requestAnimationFrame(tick);
}

function startActiveLifecycle(fromQueue = false, queueOffset = 0) {
  if (!activeEntry?.el) return;
  const el = activeEntry.el;
  clearTimers();

  el.classList.remove('is-queued');
  el.style.transition = 'none';
  if (fromQueue) {
    el.style.opacity = '0.5';
    el.style.transform = `translateY(${queueOffset}px) scale(0.85)`;
  } else {
    el.style.opacity = '0';
    el.style.transform = 'translateY(-12px) scale(0.92)';
  }

  window.requestAnimationFrame(() => {
    const duration = fromQueue ? 160 : 140;
    el.style.transition = `transform ${duration}ms ${ENTRY_EASE}, opacity ${duration}ms ${ENTRY_EASE}`;
    el.style.opacity = '1';
    el.style.transform = 'translateY(0px) scale(1)';
  });

  holdTimer = window.setTimeout(() => {
    el.style.transition = 'transform 200ms ease-in, opacity 200ms ease-in';
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px) scale(1)';
    exitTimer = window.setTimeout(() => {
      removeEntryElement(activeEntry);
      activeEntry = null;
      processQueue();
    }, 200);
  }, 140 + 1800);
}

function processQueue() {
  if (!stackEl) return;
  renderStack();
  if (activeEntry) return;
  if (queue.length === 0) return;
  if (isPaused()) {
    scheduleMonitor();
    return;
  }
  const next = queue.shift();
  const queueOffset = next?.queueOffset || STACK_GAP_PX;
  activeEntry = next || null;
  renderStack();
  startActiveLifecycle(true, queueOffset);
}

function tryConsolidate(entry) {
  const now = performance.now();
  const candidates = [];
  if (activeEntry) candidates.push(activeEntry);
  if (queue.length > 0) candidates.push(queue[queue.length - 1]);

  for (const candidate of candidates) {
    if (!candidate) continue;
    const withinWindow = now - candidate.lastUpdatedAt <= CONSOLIDATION_WINDOW_MS;
    if (!withinWindow || candidate.key !== entry.key) continue;
    candidate.count += 1;
    candidate.lastUpdatedAt = now;
    if (candidate.nameEl) candidate.nameEl.textContent = toDisplayName(candidate);
    return true;
  }
  return false;
}

/**
 * Show a center-screen pickup notification with queued stacking behavior.
 * @param {{ icon?: string, label?: string, itemName?: string, rarity?: string }} data
 */
export function showPickupNotification(data) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  injectStyles();
  ensureStack();

  const entry = normalizeEntry(data);
  if (tryConsolidate(entry)) {
    renderStack();
    return;
  }

  if (!activeEntry && !isPaused()) {
    activeEntry = entry;
    renderStack();
    startActiveLifecycle(false, 0);
    return;
  }

  if (queue.length >= MAX_QUEUE) {
    const removed = queue.shift();
    removeEntryElement(removed);
  }
  queue.push(entry);
  renderStack();
  processQueue();
}

showPickupNotification.setPausedResolver = (resolver) => {
  pausedResolver = typeof resolver === 'function' ? resolver : null;
  if (!isPaused()) processQueue();
};
